import { type TablePaginationConfig } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CardComponent } from "../../../../../components/Card/CardComponent"
import { EntityTable } from "../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../../../components/Table/TableResultCount.tsx"
import { useInfiniteScroll } from "../../../../../hooks/use-table-infinite-scroll.tsx"
import { cn } from "../../../../../utils/cn.ts"
import { getTableOrderingFromSorter } from "../../../../../utils/table"
import { type ProjectTypesFilters } from "../../../redux/templates.types.ts"
import { type ProjectTypeTemplate } from "../../../types/project-type.types.ts"
import { ProjectTypesTemplatesFilters } from "./ProjectTypesTemplatesFilters.tsx"
import { getColumns } from "./columns.tsx"

interface ProjectTypesTemplatesProps {
  loading: boolean
  templates: ProjectTypeTemplate[]
  count: number
  next?: string | null
  retryNext?: string | null
  filters: ProjectTypesFilters
  //
  deleteAllowed?: boolean
  copyAllowed?: boolean
  editAllowed?: boolean
  //
  onLoadMore: () => void
  onOrderingChange: (ordering: string | null) => void
  onSearchChange: (search: string | null) => void
  onSelectRow: (record: ProjectTypeTemplate, action: string) => void
}

export function ProjectTypesTemplates({
  loading,
  templates,
  count,
  next,
  retryNext,
  filters,
  deleteAllowed,
  editAllowed,
  copyAllowed,
  onLoadMore,
  onOrderingChange,
  onSearchChange,
  onSelectRow
}: ProjectTypesTemplatesProps) {
  const { t } = useTranslation()

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: 1000,
        y: "calc(100vh - 64px - 30px - 40px - 20px - 70px - 24px - 55px - 60px)",
        scrollToFirstRowOnChange: true
      },
      onRow: (record: ProjectTypeTemplate) => {
        return {
          onDoubleClick: editAllowed
            ? () => {
                onSelectRow(record, "view")
              }
            : undefined
        }
      }
    }),
    [editAllowed, onSelectRow]
  )

  const { onTableScroll, containerRef } = useInfiniteScroll({
    loading,
    hasMore: !!next,
    loadMoreKey: next,
    loadMore: onLoadMore
  })

  const handleChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter:
        | SorterResult<ProjectTypeTemplate>
        | SorterResult<ProjectTypeTemplate>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  const tableColumns = useMemo(() => {
    return getColumns({
      t,
      onSelectRow,
      editAllowed,
      copyAllowed,
      deleteAllowed
    })
  }, [t, onSelectRow, editAllowed, copyAllowed, deleteAllowed])

  return (
    <CardComponent classNames={{ body: "p-0" }}>
      <ProjectTypesTemplatesFilters
        filters={filters}
        onSearchChange={onSearchChange}
      />
      <div ref={containerRef}>
        <EntityTable<ProjectTypeTemplate>
          surface={TABLE_SURFACES.projectTypeTemplates}
          selectionMode="on-demand"
          tab={DEFAULT_TAB}
          hideConfigButton
          columns={tableColumns}
          loading={loading && !templates?.length}
          dataSource={templates}
          className={cn({
            "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
              !templates?.length
          })}
          scroll={tableSettings.scroll}
          onChange={handleChangeTable}
          onScroll={onTableScroll}
          withOutPagination
          pagination={false}
          onRow={tableSettings.onRow}
          rowHoverable={true}
        />
        <TableResultCount
          loaded={templates?.length ?? 0}
          total={count}
          loading={loading}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
      </div>
    </CardComponent>
  )
}
